import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

interface CancelRow {
  name: string;
  email: string;
  plan: string;
  joined_at: string;
  started_at: string;
  cancelled_at: string;
  access_until: string | null;
  days_held: number;
  reason: string;
  outcome: string;
  payments: number;
  revenue_usd: number;
  in_app: boolean;
}
interface Summary {
  total: number;
  with_reason: number;
  saved: number;
  paid: number;
  trial_only: number;
  lost_revenue_usd: number;
  reasons: Record<string, number>;
}
interface SavedRow {
  name: string;
  email: string;
  plan: string;
  reason: string;
  accepted_at: string;
  access_until: string | null;
  payments: number;
  revenue_usd: number;
  still_active: boolean;
}
interface Resp { cancellations: CancelRow[]; saved: SavedRow[]; summary: Summary }

// The app sends camelCase enum values; spell them out for humans.
const REASON_LABEL: Record<string, string> = {
  tooExpensive: 'Too expensive',
  notUsingEnough: 'Not using it enough',
  technicalIssues: 'Technical issues',
  foundAlternative: 'Found an alternative',
  other: 'Other',
};
const REASON_COLOR: Record<string, string> = {
  tooExpensive: '#FBBF24',
  notUsingEnough: '#60A5FA',
  technicalIssues: '#F87171',
  foundAlternative: '#A78BFA',
  other: '#8a8a90',
};
const OUTCOME_LABEL: Record<string, string> = {
  offer_shown: 'Offer shown',
  offer_declined: 'Declined offer',
  offer_accepted: 'Accepted offer',
};

// Device-only accounts have a UUID for an email and, with no display name,
// a UUID for a name too. Rendered raw they blow the column open, so they
// collapse to "Guest" plus a short id; the full value stays in the tooltip.
const GUEST_DOMAIN = '@device.gardenoai.com';

function identity(name: string, email: string): { label: string; sub: string; title: string } {
  if (email.endsWith(GUEST_DOMAIN)) {
    return { label: 'Guest', sub: email.slice(0, 8).toUpperCase(), title: email };
  }
  return { label: name, sub: email, title: email };
}

function when(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'Asia/Karachi' });
}

function CancellationsTab({ token, isMobile }: { token: string; isMobile: boolean }) {
  const [data, setData] = useState<Resp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onlyWithReason, setOnlyWithReason] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/cancellations`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setData((d.data ?? d) as Resp);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const th: React.CSSProperties = {
    textAlign: 'right', padding: '11px 10px', fontSize: 11.5, color: C.sub,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.35, verticalAlign: 'bottom',
  };
  const td: React.CSSProperties = {
    textAlign: 'right', padding: '11px 10px', fontSize: 13.5, color: C.text,
    borderTop: `1px solid ${C.border}`, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  };
  const card: React.CSSProperties = {
    background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px',
  };

  const s = data?.summary;
  const rows = (data?.cancellations ?? []).filter((r) => !onlyWithReason || r.reason);
  const capturePct = s && s.total > 0 ? Math.round((s.with_reason / s.total) * 100) : 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: C.text }}>Cancellations</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginLeft: 'auto', color: C.sub, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyWithReason} onChange={(e) => setOnlyWithReason(e.target.checked)} />
          Only those who gave a reason
        </label>
        <button onClick={load} style={{ background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', cursor: 'pointer', color: C.text, fontSize: 13.5 }}>Refresh</button>
      </div>
      <p style={{ color: C.sub, margin: '6px 0 16px', fontSize: 13, lineHeight: 1.6, maxWidth: 860 }}>
        A reason only exists when someone cancels inside the app, where the survey and retention offer live.
        Cancelling through iOS Settings bypasses that entirely and Apple never tells us why, which is why most
        rows below are blank.
      </p>

      {error && <div style={{ color: '#F87171', fontSize: 13.5, marginBottom: 12 }}>{error}</div>}
      {loading && !data && <div style={{ color: C.sub, fontSize: 13.5 }}>Loading…</div>}

      {s && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: 12, marginBottom: 18 }}>
            {[
              { label: 'Cancelled', value: String(s.total) },
              { label: 'Gave a reason', value: `${s.with_reason} (${capturePct}%)`, color: capturePct < 30 ? '#FBBF24' : undefined },
              { label: 'Saved (still on)', value: String(s.saved), color: s.saved ? '#34D399' : undefined },
              { label: 'Had paid', value: String(s.paid) },
              { label: 'Trial only', value: String(s.trial_only) },
            ].map((k) => (
              <div key={k.label} style={card}>
                <div style={{ color: C.sub, fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, marginTop: 7, color: k.color || C.text }}>{k.value}</div>
              </div>
            ))}
          </div>

          {Object.keys(s.reasons).length > 0 && (
            <div style={{ ...card, marginBottom: 18 }}>
              <div style={{ color: C.sub, fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 12 }}>Reasons given</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {Object.entries(s.reasons).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                  <div key={reason} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 13px', borderRadius: 20,
                    background: C.panelAlt, border: `1px solid ${REASON_COLOR[reason] ?? C.border}44`,
                  }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: REASON_COLOR[reason] ?? C.sub }} />
                    <span style={{ fontSize: 13, color: C.text }}>{REASON_LABEL[reason] ?? reason}</span>
                    <strong style={{ fontSize: 13, color: REASON_COLOR[reason] ?? C.sub }}>{count}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(data?.saved ?? []).length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, margin: '0 0 4px', color: '#34D399' }}>
                Accepted the retention offer
              </h3>
              <p style={{ color: C.sub, fontSize: 12.5, margin: '0 0 10px' }}>
                They went into the cancel flow, took the discount and stayed, so they are not in the list below.
              </p>
              <div style={{ background: C.panel, border: '1px solid #34D39944', borderRadius: 14, overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 700 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: 'left' }}>User</th>
                      <th style={{ ...th, textAlign: 'left' }}>Plan</th>
                      <th style={th}>Accepted</th>
                      <th style={{ ...th, textAlign: 'left' }}>Reason given</th>
                      <th style={{ ...th, textAlign: 'left' }}>Status</th>
                      <th style={th}>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.saved ?? []).map((r) => (
                      <tr key={r.email + r.accepted_at}>
                        <td style={{ ...td, textAlign: 'left', maxWidth: 260, whiteSpace: 'normal' }}
                            title={identity(r.name, r.email).title}>
                          <div style={{ fontWeight: 600 }}>{identity(r.name, r.email).label}</div>
                          <div style={{ fontSize: 11.5, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
                            {identity(r.name, r.email).sub}
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'left', color: r.plan === 'annual' ? '#34D399' : C.sub }}>{r.plan}</td>
                        <td style={td}>{when(r.accepted_at)}</td>
                        <td style={{ ...td, textAlign: 'left' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                            background: `${REASON_COLOR[r.reason] ?? C.sub}22`,
                            color: REASON_COLOR[r.reason] ?? C.sub,
                          }}>{REASON_LABEL[r.reason] ?? r.reason}</span>
                        </td>
                        <td style={{ ...td, textAlign: 'left', fontSize: 12.5, color: r.still_active ? '#34D399' : '#FBBF24' }}>
                          {r.still_active ? 'Still subscribed' : 'Lapsed since'}
                        </td>
                        <td style={{ ...td, color: r.revenue_usd > 0 ? '#34D399' : C.sub }}>
                          {r.revenue_usd > 0 ? `$${r.revenue_usd.toFixed(2)}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <h3 style={{ fontSize: 15, fontWeight: 800, margin: '0 0 10px' }}>Cancelled</h3>
          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 860 }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>User</th>
                  <th style={{ ...th, textAlign: 'left' }}>Plan</th>
                  <th style={th}>Cancelled</th>
                  <th style={th}>Held for</th>
                  <th style={{ ...th, textAlign: 'left' }}>Reason</th>
                  <th style={{ ...th, textAlign: 'left' }}>Outcome</th>
                  <th style={th}>Paid</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.email + r.cancelled_at}>
                    <td style={{ ...td, textAlign: 'left', maxWidth: 260, whiteSpace: 'normal' }}
                        title={identity(r.name, r.email).title}>
                      <div style={{ fontWeight: 600 }}>{identity(r.name, r.email).label}</div>
                      <div style={{
                        fontSize: 11.5, color: C.sub, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240,
                      }}>{identity(r.name, r.email).sub}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'left', color: r.plan === 'annual' ? '#34D399' : C.sub }}>{r.plan}</td>
                    <td style={td}>{when(r.cancelled_at)}</td>
                    <td style={{ ...td, color: r.days_held < 2 ? '#F87171' : C.text }}>
                      {r.days_held < 1 ? `${Math.round(r.days_held * 24)}h` : `${Math.round(r.days_held)}d`}
                    </td>
                    <td style={{ ...td, textAlign: 'left' }}>
                      {r.reason ? (
                        <span style={{
                          padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                          background: `${REASON_COLOR[r.reason] ?? C.sub}22`,
                          color: REASON_COLOR[r.reason] ?? C.sub,
                        }}>{REASON_LABEL[r.reason] ?? r.reason}</span>
                      ) : (
                        <span style={{ color: '#5a5a60', fontSize: 12.5 }}>via iOS Settings, not asked</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'left', fontSize: 12.5, color: r.outcome === 'offer_accepted' ? '#34D399' : C.sub }}>
                      {OUTCOME_LABEL[r.outcome] ?? '—'}
                    </td>
                    <td style={{ ...td, color: r.payments > 0 ? '#34D399' : C.sub }}>
                      {r.payments > 0 ? `$${r.revenue_usd.toFixed(2)}` : 'trial'}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: C.sub }}>Nothing to show</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default CancellationsTab;
